import React, { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  PlayerDto,
  PlayerPosition,
  useCreatePlayer,
  useDeletePlayer,
  usePlayers,
  useUpdatePlayer,
} from '@shared/api/hooks/usePlayers';
import { MediaSelector } from '@shared/components/MediaSelector';

const POSITION_COLORS: Record<PlayerPosition, string> = {
  GK: 'gold',
  DEF: 'blue',
  MID: 'green',
  FWD: 'red',
};

interface FormValues {
  firstName: string;
  lastName: string;
  number: number;
  position: PlayerPosition;
  nationality: string;
  dateOfBirth: Dayjs;
  bioIt?: string;
  bioEn?: string;
  height?: number;
  weight?: number;
  photoUrl?: string;
  isActive?: boolean;
}

export function PlayersPage(): React.ReactElement {
  const [filterPos, setFilterPos] = useState<PlayerPosition | undefined>(undefined);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form] = Form.useForm<FormValues>();

  const { data: players = [], isLoading, refetch, isFetching } = usePlayers({
    position: filterPos,
    active: filterActive,
  });
  const createMutation = useCreatePlayer();
  const updateMutation = useUpdatePlayer();
  const deleteMutation = useDeletePlayer();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    setModalOpen(true);
  };
  const openEdit = (p: PlayerDto): void => {
    setEditId(p.id);
    const bio = (p.bio ?? {}) as { it?: string; en?: string };
    form.setFieldsValue({
      firstName: p.firstName,
      lastName: p.lastName,
      number: p.number,
      position: p.position,
      nationality: p.nationality,
      dateOfBirth: p.dateOfBirth ? dayjs(p.dateOfBirth) : undefined,
      bioIt: bio.it,
      bioEn: bio.en,
      height: p.height ?? undefined,
      weight: p.weight ?? undefined,
      photoUrl: p.photoUrl ?? undefined,
      isActive: p.isActive,
    });
    setModalOpen(true);
  };

  const onSubmit = async (): Promise<void> => {
    const v = await form.validateFields();
    const bio = v.bioIt || v.bioEn ? { it: v.bioIt ?? '', en: v.bioEn ?? '' } : undefined;
    const input = {
      firstName: v.firstName,
      lastName: v.lastName,
      number: v.number,
      position: v.position,
      nationality: v.nationality,
      dateOfBirth: v.dateOfBirth.format('YYYY-MM-DD'),
      bio,
      height: v.height,
      weight: v.weight,
      photoUrl: v.photoUrl || undefined,
    };
    try {
      if (editId) {
        await updateMutation.mutateAsync({
          id: editId,
          input: { ...input, isActive: v.isActive },
        });
        message.success('Giocatore aggiornato');
      } else {
        await createMutation.mutateAsync(input);
        message.success('Giocatore creato');
      }
      setModalOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore di salvataggio');
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Giocatore eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore eliminazione');
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Rosa
          </Typography.Title>
          <Tag>{players.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Select
            placeholder="Ruolo"
            style={{ width: 120 }}
            allowClear
            value={filterPos}
            onChange={(v) => setFilterPos(v as PlayerPosition | undefined)}
            options={[
              { value: 'GK', label: 'Portiere' },
              { value: 'DEF', label: 'Difensore' },
              { value: 'MID', label: 'Centrocampista' },
              { value: 'FWD', label: 'Attaccante' },
            ]}
          />
          <Select
            style={{ width: 130 }}
            value={filterActive ?? 'all'}
            onChange={(v) => setFilterActive(v === 'all' ? undefined : v === 'true')}
            options={[
              { value: 'true', label: 'Solo attivi' },
              { value: 'false', label: 'Solo inattivi' },
              { value: 'all', label: 'Tutti' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo giocatore
          </Button>
        </Space>
      }
    >
      <Table<PlayerDto>
        rowKey="id"
        loading={isLoading}
        dataSource={players}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: '#',
            dataIndex: 'number',
            width: 60,
            sorter: (a, b) => a.number - b.number,
          },
          {
            title: 'Giocatore',
            render: (_v, r) => (
              <Space>
                {r.photoUrl ? (
                  <img
                    src={r.photoUrl}
                    alt=""
                    width={32}
                    height={32}
                    style={{ borderRadius: 16, objectFit: 'cover' }}
                  />
                ) : null}
                <span>
                  {r.firstName} {r.lastName}
                </span>
              </Space>
            ),
          },
          {
            title: 'Ruolo',
            dataIndex: 'position',
            width: 100,
            render: (v: PlayerPosition) => <Tag color={POSITION_COLORS[v]}>{v}</Tag>,
          },
          { title: 'Nazionalità', dataIndex: 'nationality', width: 130 },
          {
            title: 'Nato il',
            dataIndex: 'dateOfBirth',
            width: 130,
            render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            width: 100,
            render: (v: boolean) =>
              v ? <Tag color="green">Attivo</Tag> : <Tag color="default">Inattivo</Tag>,
          },
          {
            title: 'Stats stagione',
            render: (_v, r) =>
              r.stats ? (
                <Typography.Text type="secondary">
                  {r.stats.appearances} pres · {r.stats.goals} gol · {r.stats.assists} assist
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: '',
            width: 90,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare questo giocatore?"
                  okText="Elimina"
                  cancelText="Annulla"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editId ? 'Modifica giocatore' : 'Nuovo giocatore'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        okText={editId ? 'Salva' : 'Crea'}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnHidden
        width={640}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="firstName"
              label="Nome"
              rules={[{ required: true, max: 100 }]}
              style={{ flex: 1, marginRight: 8 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Cognome"
              rules={[{ required: true, max: 100 }]}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </Space.Compact>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="number"
              label="Numero maglia"
              rules={[{ required: true, type: 'number', min: 1, max: 99 }]}
              style={{ width: 130, marginRight: 8 }}
            >
              <InputNumber min={1} max={99} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="position"
              label="Ruolo"
              rules={[{ required: true }]}
              style={{ width: 180, marginRight: 8 }}
            >
              <Select
                options={[
                  { value: 'GK', label: 'GK · Portiere' },
                  { value: 'DEF', label: 'DEF · Difensore' },
                  { value: 'MID', label: 'MID · Centrocampo' },
                  { value: 'FWD', label: 'FWD · Attaccante' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="nationality"
              label="Nazionalità"
              rules={[{ required: true, max: 100 }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="Italia" />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="dateOfBirth" label="Data di nascita" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} placeholder="Seleziona data" />
          </Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="height" label="Altezza (cm)" style={{ flex: 1, marginRight: 8 }}>
              <InputNumber min={140} max={220} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="weight" label="Peso (kg)" style={{ flex: 1 }}>
              <InputNumber min={40} max={150} style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="photoUrl" label="Foto giocatore">
            <MediaSelector category="PLAYER_PHOTO" placeholder="Seleziona foto dalla libreria" />
          </Form.Item>
          <Form.Item name="bioIt" label="Bio (IT)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="bioEn" label="Bio (EN)">
            <Input.TextArea rows={2} />
          </Form.Item>
          {editId && (
            <Form.Item name="isActive" label="Stato attivo" valuePropName="checked">
              <Select
                options={[
                  { value: true, label: 'Attivo' },
                  { value: false, label: 'Inattivo' },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
}
